// src/components/roads/ponding/RoadPondingMapLayer.js
// Draws the road ponding forecast on the Road Works Leaflet map (the instance from GisMapView's onMapReady):
//   • every ponding segment as a thick translucent line in the band colour (water on the road)
//   • the point where water settles (sag / drain) as a circle sized by depth, pulsing when ponding now
//   • a dashed line to the linked well / catch basin, red when that drain is forecast to fail
//   • hour playback: depth at the selected forecast hour (null = peak); dry segments fade out
//   • hover = tooltip, click = details popup with "Open Works Report"
// One canvas layer group, rebuilt only when the forecast / hour / visibility changes — no React element per segment.
import { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import { bandMeta, bandAt, depthAt, fmtWater, fmtHours, fmtClears, KIND_LABEL } from './pondingUi';

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const PULSE_CSS = `@keyframes fsPondPulse{0%{opacity:.85}50%{opacity:.35}100%{opacity:.85}} .fs-pond-live{animation:fsPondPulse 1.8s ease-in-out infinite}`;

function popupHtml(s, hour) {
    const b = bandMeta(s.band);
    const d = depthAt(s, hour);
    const drains = (s.drains || []).map((x) => `<li>${esc(x.name || x.asset_id)} · ${esc((x.kind || 'drain').replace('_', ' '))} · ${x.distance_m} m · <b style="color:${x.state && x.state !== 'ok' ? '#C53030' : '#2F855A'}">${esc(x.state || 'ok')}</b>${x.utilisation_pct != null ? ` · ${x.utilisation_pct}% capacity` : ''}${x.clearance_pct != null ? ` · silt ${x.clearance_pct}%` : ''}</li>`).join('');
    return `<div style="min-width:240px;max-width:300px;font:12px/1.4 system-ui,sans-serif">
      <div style="font-weight:800;font-size:14px">${esc(s.road_name || 'Road')}</div>
      <div style="color:#718096;font-size:11px;margin-bottom:6px">Segment ${esc(s.index ?? '—')} · ${s.station_start_m != null ? `${Math.round(s.station_start_m)}–${Math.round(s.station_end_m)} m` : ''}</div>
      <div style="display:flex;gap:10px;align-items:center;padding:8px;border-radius:8px;background:${b.hex}1f;border:1px solid ${b.hex};margin-bottom:6px">
        <div style="font-size:22px;font-weight:900;color:${b.hex}">${esc(fmtWater(s, hour))}</div>
        <div><div style="font-weight:800">${b.label}${hour == null ? ' · peak' : ` · hour +${hour}`}</div><div style="color:#4A5568;font-size:11px">${esc(KIND_LABEL[s.kind] || '')}</div></div>
      </div>
      <table style="width:100%;border-collapse:collapse">
        ${s.time_to_ponding_hours != null ? `<tr><td style="color:#718096">Water starts</td><td style="text-align:right;font-weight:600">${fmtHours(s.time_to_ponding_hours)}</td></tr>` : ''}
        <tr><td style="color:#718096">After the rain</td><td style="text-align:right;font-weight:600">${esc(fmtClears(s))}</td></tr>
        ${s.peak_volume_m3 != null ? `<tr><td style="color:#718096">Stored water</td><td style="text-align:right;font-weight:600">${Math.round(s.peak_volume_m3)} m³</td></tr>` : ''}
        ${s.gutter_spread_m ? `<tr><td style="color:#718096">Gutter spread</td><td style="text-align:right;font-weight:600">${s.gutter_spread_m} m</td></tr>` : ''}
        ${s.ground_low_m != null ? `<tr><td style="color:#718096">Ground (TessaDEM)</td><td style="text-align:right;font-weight:600">${s.ground_low_m} m</td></tr>` : ''}
      </table>
      ${s.causes?.length ? `<div style="margin-top:6px;font-weight:700">Why</div><ul style="margin:2px 0 0 16px;padding:0">${s.causes.map((c) => `<li>${esc(c)}</li>`).join('')}</ul>` : (s.cause ? `<div style="margin-top:6px">${esc(s.cause)}</div>` : '')}
      ${drains ? `<div style="margin-top:6px;font-weight:700">Drainage</div><ul style="margin:2px 0 0 16px;padding:0">${drains}</ul>` : ''}
      ${s.actions?.length ? `<div style="margin-top:6px;font-weight:700">Do</div><ul style="margin:2px 0 0 16px;padding:0">${s.actions.map((c) => `<li>${esc(c)}</li>`).join('')}</ul>` : (s.action ? `<div style="margin-top:6px"><b>Do:</b> ${esc(s.action)}</div>` : '')}
      <button type="button" data-fs-open-report="1" style="margin-top:8px;width:100%;padding:6px;background:#2B6CB0;color:#fff;border:none;border-radius:6px;font-weight:700;cursor:pointer">Open Works Report</button>
    </div>`;
}

export default function RoadPondingMapLayer({ map, forecast, hour = null, visible = true, selectedSegmentId = null, onSelectSegment, onOpenReport }) {
    const groupRef = useRef(null);
    const cbRef = useRef({}); cbRef.current = { onSelectSegment, onOpenReport };
    const renderer = useMemo(() => L.canvas({ padding: 0.5, tolerance: 6 }), []);
    // settling points use SVG so the "ponding now" pulse (CSS class) works; lines stay on canvas (thousands of them)
    const pointRenderer = useMemo(() => L.svg({ padding: 0.5 }), []);

    useEffect(() => {
        if (typeof document === 'undefined' || document.getElementById('fs-pond-css')) return;
        const st = document.createElement('style'); st.id = 'fs-pond-css'; st.textContent = PULSE_CSS; document.head.appendChild(st);
    }, []);

    useEffect(() => {
        if (!map) return undefined;
        const group = L.layerGroup();
        groupRef.current = group;
        if (visible && forecast?.segments?.length) {
            for (const s of forecast.segments) {
                const band = bandAt(s, hour);
                const b = bandMeta(s.band);
                const dry = band === 'none';
                if (s.start && s.end) {
                    const line = L.polyline([[s.start.lat, s.start.lng], [s.end.lat, s.end.lng]], {
                        renderer, color: b.hex, weight: dry ? 4 : bandMeta(band).weight + 4, opacity: dry ? 0.25 : 0.55, lineCap: 'round',
                    });
                    line.bindTooltip(`<b>${esc(s.road_name)}</b> · seg ${esc(s.index ?? '—')}<br/>${esc(fmtWater(s, hour))} ${hour == null ? 'peak' : `at +${hour} h`} · ${esc(KIND_LABEL[s.kind] || '')}`, { sticky: true, direction: 'top' });
                    line.on('click', (e) => { L.DomEvent.stopPropagation(e); cbRef.current.onSelectSegment?.(s); });
                    group.addLayer(line);
                }
                const pt = s.point || (s.start && s.end ? { lat: (s.start.lat + s.end.lat) / 2, lng: (s.start.lng + s.end.lng) / 2 } : null);
                if (!pt) continue;
                const drain = s.drains?.[0] || s.drain || null;
                if (drain?.latitude != null && !dry) {
                    const failing = drain.state && drain.state !== 'ok';
                    group.addLayer(L.polyline([[pt.lat, pt.lng], [drain.latitude, drain.longitude]], { renderer, color: failing ? '#E53E3E' : '#38A169', weight: 2, dashArray: '4 6', opacity: 0.9, interactive: false }));
                }
                const d = depthAt(s, hour);
                const r = dry ? 4 : Math.min(18, 6 + d * 40);
                const live = !dry && hour == null && s.time_to_ponding_hours != null && s.time_to_ponding_hours <= 1;
                const c = L.circleMarker([pt.lat, pt.lng], {
                    renderer: pointRenderer, radius: r, color: '#fff', weight: 2, fillColor: bandMeta(band === 'none' ? s.band : band).hex, fillOpacity: dry ? 0.3 : 0.85,
                    className: live ? 'fs-pond-live' : undefined,
                });
                c.bindTooltip(`<b>${esc(s.road_name)}</b><br/>${esc(fmtWater(s, hour))} · ${esc(fmtClears(s))}`, { direction: 'top' });
                c.bindPopup(() => {
                    const div = document.createElement('div');
                    div.innerHTML = popupHtml(s, hour);
                    div.querySelector('[data-fs-open-report]')?.addEventListener('click', () => cbRef.current.onOpenReport?.(s));
                    return div;
                }, { maxWidth: 320 });
                c.on('click', () => cbRef.current.onSelectSegment?.(s));
                c._fsSegId = s.segment_id;
                group.addLayer(c);
            }
        }
        // the map may have been torn down (tab switch / GisMapView remount) — never let the layer crash the page
        try { if (map._container || map.getContainer?.()) group.addTo(map); } catch (_) { /* map gone */ }
        return () => { try { group.remove(); } catch (_) { /* map gone */ } if (groupRef.current === group) groupRef.current = null; };
    }, [map, forecast, hour, visible, renderer, pointRenderer]);

    // selected segment → open its popup (and bring it forward)
    useEffect(() => {
        const g = groupRef.current;
        if (!g || selectedSegmentId == null) return;
        g.eachLayer((l) => { if (l._fsSegId === String(selectedSegmentId)) { l.bringToFront?.(); l.openPopup?.(); } });
    }, [selectedSegmentId, forecast, hour]);

    return null;
}
